// src/pages/calidad/BpmIncidenciasCatalogo.jsx
// Catálogo de incidencias BPM: rubros que se revisan a cada persona, con su
// nivel de riesgo y puntuación. Muestra cuánto se usa cada uno.
import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { FiDownload, FiInfo, FiPlus } from 'react-icons/fi';
import api from '../../services/api';
import s from '../mantenimiento/estilos';
import { TagNivel } from './BpmFichaPersona';

const NIVELES = ['BAJO', 'MEDIO', 'ALTO'];
const PUNTOS_SUGERIDOS = { BAJO: 1, MEDIO: 3, ALTO: 6 };
const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function BpmIncidenciasCatalogo() {
  const [filas, setFilas] = useState([]);
  const [originales, setOriginales] = useState({});
  const [uso, setUso] = useState({});
  const [diasUso, setDiasUso] = useState(90);
  const [cargando, setCargando] = useState(true);
  const [guardandoId, setGuardandoId] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [nueva, setNueva] = useState({ incidencia: '', nivel_riesgo: 'BAJO', puntuacion: 1 });
  const [creando, setCreando] = useState(false);
  const [filtro, setFiltro] = useState({ busqueda: '', nivel: '', estado: 'activos' });

  async function cargarUso(d = diasUso) {
    try {
      const r = await api.get('/api/calidad/bpm-analisis/uso-catalogo', { params: { dias: d } });
      setUso(Object.fromEntries(r.data.data.map((u) => [u.id, u])));
    } catch (err) {
      console.error(err); // si la ruta de análisis no está montada, el catálogo funciona igual
    }
  }

  async function cargar() {
    try {
      setCargando(true);
      const res = await api.get('/api/calidad/bpm-incidencias');
      setFilas(res.data.data);
      setOriginales(Object.fromEntries(res.data.data.map((f) => [f.id, { ...f }])));
      await cargarUso();
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cambio = (f) => {
    const o = originales[f.id];
    return o && (o.nivel_riesgo !== f.nivel_riesgo || Number(o.puntuacion) !== Number(f.puntuacion) || o.activo !== f.activo);
  };

  function handleCambiar(id, campo, valor) {
    setFilas((prev) => prev.map((f) => (f.id === id ? { ...f, [campo]: valor } : f)));
  }

  const errorTexto = (err, def) => (err.response?.status === 403 ? 'No tienes permiso para modificar el catálogo.' : err.response?.data?.error || def);

  async function handleGuardar(f, cambiosExtra = {}) {
    const fila = { ...f, ...cambiosExtra };
    if (!Number.isInteger(Number(fila.puntuacion)) || Number(fila.puntuacion) < 1) {
      setMensaje({ tipo: 'error', texto: 'La puntuación debe ser un número entero mayor a 0.' });
      return;
    }
    try {
      setGuardandoId(f.id);
      setMensaje(null);
      const res = await api.put(`/api/calidad/bpm-incidencias/${f.id}`, {
        nivel_riesgo: fila.nivel_riesgo,
        puntuacion: Number(fila.puntuacion),
        activo: fila.activo,
      });
      const guardada = res.data.data;
      setFilas((prev) => prev.map((x) => (x.id === f.id ? guardada : x)));
      setOriginales((prev) => ({ ...prev, [f.id]: { ...guardada } }));
      setMensaje({ tipo: 'ok', texto: `✓ ${fila.incidencia} ${cambiosExtra.activo === false ? 'desactivada' : cambiosExtra.activo === true ? 'activada' : 'actualizada'}.` });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: errorTexto(err, `No se pudo guardar ${fila.incidencia}.`) });
    } finally {
      setGuardandoId(null);
    }
  }

  async function handleCrear() {
    if (!nueva.incidencia.trim()) {
      setMensaje({ tipo: 'error', texto: 'Escribe el nombre de la incidencia.' });
      return;
    }
    try {
      setCreando(true);
      setMensaje(null);
      await api.post('/api/calidad/bpm-incidencias', {
        incidencia: nueva.incidencia.trim(),
        nivel_riesgo: nueva.nivel_riesgo,
        puntuacion: Number(nueva.puntuacion),
      });
      setNueva({ incidencia: '', nivel_riesgo: 'BAJO', puntuacion: 1 });
      setMensaje({ tipo: 'ok', texto: '✓ Incidencia agregada.' });
      cargar();
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: errorTexto(err, 'No se pudo crear la incidencia.') });
    } finally {
      setCreando(false);
    }
  }

  const visibles = useMemo(() => filas.filter((f) => {
    if (filtro.estado === 'activos' && !f.activo) return false;
    if (filtro.estado === 'inactivos' && f.activo) return false;
    if (filtro.nivel && f.nivel_riesgo !== filtro.nivel) return false;
    const q = filtro.busqueda.trim().toLowerCase();
    return !q || f.incidencia.toLowerCase().includes(q);
  }).sort((a, b) => (uso[b.id]?.veces_periodo || 0) - (uso[a.id]?.veces_periodo || 0) || a.incidencia.localeCompare(b.incidencia, 'es')), [filas, filtro, uso]);

  const activas = filas.filter((f) => f.activo);
  const conteoNivel = (n) => activas.filter((f) => f.nivel_riesgo === n).length;
  const sinUso = activas.filter((f) => !(uso[f.id]?.veces_periodo)).length;

  function excel() {
    const ws = XLSX.utils.json_to_sheet(filas.map((f) => ({
      Incidencia: f.incidencia, Nivel: f.nivel_riesgo, Puntos: f.puntuacion, Activa: f.activo ? 'Sí' : 'No',
      [`Veces (últimos ${diasUso} días)`]: uso[f.id]?.veces_periodo ?? '', 'Veces (total)': uso[f.id]?.veces_total ?? '',
      'Última vez': fmtFecha(uso[f.id]?.ultima_vez),
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Catálogo BPM');
    XLSX.writeFile(wb, 'Catalogo_incidencias_BPM.xlsx');
  }

  if (cargando) return <div style={s.paginaContenedor}>Cargando...</div>;

  return (
    <div style={s.paginaContenedor} className="contenedor-captura">
      <div style={{ ...s.headerFila, flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>Catálogo de Incidencias BPM</h1>
          <div style={{ fontSize: 12, color: '#8A7690' }}>Rubros que se revisan a cada persona, con su nivel de riesgo y puntuación.</div>
        </div>
        <button onClick={excel} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}><FiDownload /> Descargar Excel</button>
      </div>

      {mensaje && <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>{mensaje.texto}</div>}

      <div style={s.kgrid}>
        <div style={{ ...s.kcard, borderTop: '3px solid #4A1E5C' }}>
          <div style={s.kLbl}>Rubros activos</div>
          <div style={{ ...s.kVal, fontSize: 30 }}>{activas.length}</div>
          <div style={s.kSub}>{filas.length - activas.length} inactivos</div>
        </div>
        {NIVELES.slice().reverse().map((n) => (
          <div key={n} style={{ ...s.kcard, borderTop: `3px solid ${n === 'ALTO' ? '#B23A48' : n === 'MEDIO' ? '#B2662A' : '#3E7C5A'}`, cursor: 'pointer' }}
            onClick={() => setFiltro({ ...filtro, nivel: filtro.nivel === n ? '' : n })}>
            <div style={s.kLbl}>Riesgo {n}</div>
            <div style={{ ...s.kVal, fontSize: 30 }}>{conteoNivel(n)}</div>
            <div style={s.kSub}>rubros activos</div>
          </div>
        ))}
      </div>

      <div style={{ ...s.tarjeta, borderTop: '3px dashed #D6A644' }}>
        <div style={s.seccionTitulo}>Agregar incidencia</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ ...s.filtroGrupo, flex: 1, minWidth: 220 }}>
            <span style={s.filtroLabel}>Incidencia</span>
            <input type="text" placeholder="Ej. Uso incorrecto de cubrebocas" value={nueva.incidencia}
              onChange={(e) => setNueva({ ...nueva, incidencia: e.target.value })} style={s.filtroInput} />
          </div>
          <div style={s.filtroGrupo}>
            <span style={s.filtroLabel}>Nivel de riesgo</span>
            <select value={nueva.nivel_riesgo} onChange={(e) => setNueva({ ...nueva, nivel_riesgo: e.target.value, puntuacion: PUNTOS_SUGERIDOS[e.target.value] })} style={s.filtroInput}>
              {NIVELES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={s.filtroGrupo}>
            <span style={s.filtroLabel}>Puntos</span>
            <input type="number" min="1" step="1" value={nueva.puntuacion} onChange={(e) => setNueva({ ...nueva, puntuacion: e.target.value })} style={{ ...s.filtroInput, width: 70 }} />
          </div>
          <button onClick={handleCrear} disabled={creando} style={{ ...s.botonBuscar2, display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiPlus /> {creando ? 'Agregando...' : 'Agregar'}
          </button>
        </div>
      </div>

      <div style={s.filtrosBar}>
        <div style={s.filtroGrupo}>
          <span style={s.filtroLabel}>Buscar</span>
          <input type="text" placeholder="Nombre de la incidencia…" value={filtro.busqueda} onChange={(e) => setFiltro({ ...filtro, busqueda: e.target.value })} style={{ ...s.filtroInput, minWidth: 220 }} />
        </div>
        <div style={s.filtroGrupo}>
          <span style={s.filtroLabel}>Nivel</span>
          <select value={filtro.nivel} onChange={(e) => setFiltro({ ...filtro, nivel: e.target.value })} style={s.filtroInput}>
            <option value="">Todos</option>
            {NIVELES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div style={s.filtroGrupo}>
          <span style={s.filtroLabel}>Mostrar</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['activos', 'Activos'], ['inactivos', 'Inactivos'], ['todos', 'Todos']].map(([k, label]) => (
              <button key={k} onClick={() => setFiltro({ ...filtro, estado: k })} style={{ ...s.subTabBtn, ...(filtro.estado === k ? s.subTabBtnActivo : {}) }}>{label}</button>
            ))}
          </div>
        </div>
        <div style={s.filtroGrupo}>
          <span style={s.filtroLabel}>Uso en los últimos</span>
          <select value={diasUso} onChange={(e) => { const d = Number(e.target.value); setDiasUso(d); cargarUso(d); }} style={s.filtroInput}>
            {[30, 90, 180, 365].map((d) => <option key={d} value={d}>{d} días</option>)}
          </select>
        </div>
      </div>

      {sinUso > 0 && (
        <div style={{ ...s.avisoAdvertencia, marginTop: 0, marginBottom: 10 }}>
          {sinUso} rubro(s) activo(s) no se han capturado en los últimos {diasUso} días.
        </div>
      )}

      <div style={s.tablaScroll}>
        <table style={s.tabla}>
          <thead>
            <tr>
              <th style={s.th}>Incidencia</th>
              <th style={s.th}>Nivel</th>
              <th style={s.th}>Puntos</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Veces ({diasUso} días)</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Total</th>
              <th style={s.th}>Última vez</th>
              <th style={s.th}>Activa</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {visibles.length === 0 && <tr><td colSpan={8} style={{ ...s.td, color: '#8A7690', padding: 16 }}>Sin incidencias con estos filtros.</td></tr>}
            {visibles.map((f) => {
              const u = uso[f.id] || {};
              const modificada = cambio(f);
              return (
                <tr key={f.id} style={!f.activo ? { opacity: 0.55 } : undefined}>
                  <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 220, fontWeight: 600 }}>{f.incidencia}</td>
                  <td style={s.td}>
                    <select value={f.nivel_riesgo} onChange={(e) => handleCambiar(f.id, 'nivel_riesgo', e.target.value)} style={s.inputChico}>
                      {NIVELES.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </td>
                  <td style={s.td}>
                    <input type="number" min="1" step="1" value={f.puntuacion} onChange={(e) => handleCambiar(f.id, 'puntuacion', e.target.value)} style={{ ...s.inputChico, width: 60 }} />
                  </td>
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 700 }}>{u.veces_periodo ?? '—'}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{u.veces_total ?? '—'}</td>
                  <td style={s.td}>{fmtFecha(u.ultima_vez)}</td>
                  <td style={s.td}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!f.activo} disabled={guardandoId === f.id}
                        onChange={(e) => {
                          const activo = e.target.checked;
                          if (!activo && !window.confirm(`¿Desactivar "${f.incidencia}"? Ya no aparecerá en la captura y deja de contar como rubro revisado en el % de cumplimiento (también en días anteriores, porque el % se calcula con el catálogo activo actual).`)) return;
                          handleGuardar(f, { activo });
                        }} />
                      {f.activo ? 'Sí' : 'No'}
                    </label>
                  </td>
                  <td style={s.td}>
                    {modificada && (
                      <button onClick={() => handleGuardar(f)} disabled={guardandoId === f.id} style={s.botonGuardarChico}>
                        {guardandoId === f.id ? '...' : 'Guardar'}
                      </button>
                    )}
                    {!modificada && <TagNivel nivel={f.nivel_riesgo} puntos={f.puntuacion} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ ...s.seccionKpiBloque, marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <FiInfo style={{ color: '#4A1E5C', marginTop: 2, flexShrink: 0 }} />
          <div style={s.seccionKpiFormula}>
            <strong style={{ color: '#2A1830' }}>Importante para el KPI.</strong> El % de cumplimiento a BPMs revisa a cada persona contra todos los rubros <strong>activos</strong>.
            El % se calcula con el catálogo activo de hoy, así que activar o desactivar un rubro cambia el total de rubros evaluados también en el histórico.
            Cambiar los puntos solo afecta las incidencias que se capturen a partir de ahora; las ya capturadas conservan los puntos con que se registraron.
          </div>
        </div>
      </div>
    </div>
  );
}